/**
 * Teams Page Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Main page for team management with list and CRUD operations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, AlertCircle } from 'lucide-react';
import { teamService, settingsService } from '../../services/admin';
import type { Team, TeamCreateData, TeamUpdateData, AccountSettings } from '../../types/admin';
import { TeamsList, TeamFormModal, TeamMembersModal } from '../../components/admin/teams';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';

const TeamsPage: React.FC = () => {
  // State
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [settings, setSettings] = useState<AccountSettings | null>(null);

  // Modal state
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Confirmation dialog
  const [confirmDelete, setConfirmDelete] = useState<Team | null>(null);

  // Fetch teams and settings
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [teamsResponse, settingsResponse] = await Promise.all([
        teamService.listTeams({ search: searchQuery || undefined }),
        settingsService.getAccountSettings(),
      ]);
      setTeams(teamsResponse.teams);
      setSettings(settingsResponse);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handlers
  const handleCreateOrUpdateTeam = async (data: TeamCreateData | TeamUpdateData) => {
    setIsSubmitting(true);
    try {
      if (selectedTeam) {
        await teamService.updateTeam(selectedTeam.id, data as TeamUpdateData);
        toast.success('Team updated successfully');
      } else {
        await teamService.createTeam(data as TeamCreateData);
        toast.success('Team created successfully');
      }

      setShowTeamModal(false);
      setSelectedTeam(null);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to save team'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTeam = async (team: Team) => {
    try {
      await teamService.deleteTeam(team.id);
      toast.success('Team deleted successfully');
      setConfirmDelete(null);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to delete team'));
    }
  };

  const openEditModal = (team: Team) => {
    setSelectedTeam(team);
    setShowTeamModal(true);
  };

  const openMembersModal = (team: Team) => {
    setSelectedTeam(team);
    setShowMembersModal(true);
  };

  const openClientsView = (team: Team) => {
    // For now, show a toast - in a full implementation, this would open a clients modal
    toast.info(`Viewing ${team.client_count} clients for ${team.name}`);
  };

  // Check if team management is enabled
  const isTeamManagementEnabled = settings?.enable_team_management ?? false;
  const visibilityMode = settings?.client_visibility_mode ?? 'open';

  // Calculate stats
  const activeTeams = teams.filter((t) => t.is_active).length;
  const totalMembers = teams.reduce((sum, t) => sum + t.member_count, 0);
  const totalClients = teams.reduce((sum, t) => sum + t.client_count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-gray-500 mt-1">
            Organize users into teams and manage client assignments
          </p>
        </div>
        <Button onClick={() => {
          setSelectedTeam(null);
          setShowTeamModal(true);
        }}>
          <Plus size={18} className="mr-2" />
          Create Team
        </Button>
      </div>

      {/* Warning if team management is not enabled */}
      {!isTeamManagementEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Team management is not enabled</p>
            <p className="text-sm text-amber-700 mt-1">
              Teams can be created, but they won't affect client visibility until you enable team-based visibility mode in Settings.
              Current visibility mode: <strong className="capitalize">{visibilityMode.replace(/_/g, ' ')}</strong>
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Active Teams</p>
          <p className="text-2xl font-bold text-gray-900">{activeTeams}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Members</p>
          <p className="text-2xl font-bold text-gray-900">{totalMembers}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Assigned Clients</p>
          <p className="text-2xl font-bold text-gray-900">{totalClients}</p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="relative">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search teams..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
          />
        </div>
      </div>

      {/* Teams list */}
      <TeamsList
        teams={teams}
        isLoading={isLoading}
        onEdit={openEditModal}
        onManageMembers={openMembersModal}
        onViewClients={openClientsView}
        onDelete={(team) => setConfirmDelete(team)}
      />

      {/* Team form modal */}
      <TeamFormModal
        isOpen={showTeamModal}
        onClose={() => {
          setShowTeamModal(false);
          setSelectedTeam(null);
        }}
        onSubmit={handleCreateOrUpdateTeam}
        team={selectedTeam}
        isLoading={isSubmitting}
      />

      {/* Team members modal */}
      <TeamMembersModal
        isOpen={showMembersModal}
        onClose={() => {
          setShowMembersModal(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        onMembersChanged={fetchData}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title="Delete Team"
        description={`Are you sure you want to delete the team "${confirmDelete?.name}"? This will remove all member assignments.`}
        warning={
          confirmDelete && confirmDelete.client_count > 0
            ? `This team has ${confirmDelete.client_count} client(s) assigned. You may want to reassign them to another team first.`
            : undefined
        }
        onConfirm={() => confirmDelete && handleDeleteTeam(confirmDelete)}
        confirmText="Delete"
        destructive
      />
    </div>
  );
};

export default TeamsPage;
